/**
 * Status chips.
 *
 * Colour is never the only carrier: each tone pairs with a dot and a word, so the chip still
 * reads under a colour-blind eye, a cracked screen, or the sun coming through the shop door.
 */

import type { ReactNode } from 'react';

import { cn } from './cn.js';

export type ChipTone = 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'brand';

const TONES: Record<ChipTone, string> = {
  neutral: 'bg-surface-sunken text-content-secondary',
  success: 'bg-success-subtle text-success-subtle-text',
  warning: 'bg-warning-subtle text-warning-subtle-text',
  danger: 'bg-danger-subtle text-danger-subtle-text',
  info: 'bg-info-subtle text-info-subtle-text',
  brand: 'bg-brand-subtle text-brand-subtle-text',
};

export function Chip({
  tone = 'neutral',
  dot = true,
  size = 'md',
  children,
  className,
}: {
  tone?: ChipTone;
  dot?: boolean;
  /** `sm` fits inside a 64px list row's second line without lifting it. */
  size?: 'sm' | 'md';
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-chip font-semibold',
        size === 'sm' ? 'px-2 py-0.5 text-2xs leading-4' : 'px-2.5 py-1 text-xs',
        TONES[tone],
        className,
      )}
    >
      {dot && <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-current" />}
      {children}
    </span>
  );
}

/** Stock, as a chip. Negative stock is permitted by design, so it is shown, not hidden. */
export function StockChip({
  quantity,
  threshold,
  size = 'md',
}: {
  quantity: number;
  threshold: number;
  size?: 'sm' | 'md';
}) {
  if (quantity < 0) return <Chip tone="danger" size={size}>{quantity} — needs review</Chip>;
  if (quantity === 0) return <Chip tone="danger" size={size}>Out of stock</Chip>;
  if (quantity <= threshold) return <Chip tone="warning" size={size}>{quantity} left</Chip>;
  return <Chip tone="success" size={size}>{quantity} in stock</Chip>;
}
